import React from 'react';
import { HomeIcon, MapPinIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { labPanels } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
const markerGroups = [{
  group: 'Hormones',
  markers: ['Total testosterone', 'Free testosterone', 'Estradiol', 'SHBG', 'LH', 'FSH', 'DHEA-S', 'Progesterone']
}, {
  group: 'Metabolic',
  markers: ['HbA1c', 'Fasting glucose', 'Fasting insulin', 'ApoB', 'Lp(a)', 'Full lipid panel', 'Uric acid']
}, {
  group: 'Thyroid & inflammation',
  markers: ['TSH', 'Free T3', 'Free T4', 'TPO antibodies', 'hs-CRP', 'Homocysteine', 'Ferritin']
}, {
  group: 'Organ & blood',
  markers: ['CBC with differential', 'CMP', 'eGFR', 'ALT / AST', 'PSA (men)', 'Vitamin D', 'B12']
}];
export function Labs() {
  useDocumentMeta({
    title: 'Labs & testing — Republic',
    description: 'A 62-marker baseline panel included with every membership, collected at home or at 2,100 partner draw sites.'
  });
  return <main data-id="element-398" data-mp="x7rw06xuqbq4">
      <PageHeader eyebrow="Labs & testing" title="The panel is the foundation, not an upsell." intro="Republic includes a baseline panel with every membership and annotates the results in plain language before any treatment decision is made." data-id="element-399" data-mp="x80bm6lczv50" />

      <section className="bg-cream" data-id="element-400" data-mp="4lfj32t5dczv">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-10 md:px-8 md:pb-28" data-id="element-401" data-mp="9he2ebzmc4tf">
          <img src="https://cdn.magicpatterns.com/patterns/generated-images/11bca244-30a4-406a-be5b-563ba023cc92.jpg" alt="A Republic at-home collection kit laid out on a cream surface" className="aspect-[16/7] w-full object-cover" data-id="element-402" data-mp="ehiku7k379ma" />

          <ul className="mt-14 grid gap-8 lg:grid-cols-3" data-id="element-403" data-mp="1pys0sk40bjiu">
            {labPanels.map(panel => <li key={panel.name} className="rule pt-6" data-id="element-404" data-mp="1tzvf326ep7hy">
                <div className="flex items-baseline justify-between gap-4" data-id="element-405" data-mp="3qxc7mw4rw3re">
                  <h2 className="font-serif text-2xl text-ink" data-id="element-406" data-mp="3m0db66xmzkq1">{panel.name}</h2>
                  <span className="font-serif text-3xl text-brick" data-id="element-407" data-mp="3tp7mli8j5mh">{panel.markers}</span>
                </div>
                <p className="mt-3 text-base leading-relaxed text-ink-soft" data-id="element-408" data-mp="3uzqk8yhum7a8">{panel.detail}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-409" data-mp="378bjvb9mu01m">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-28" data-id="element-410" data-mp="33a40s9rp4mn9">
          <SectionHeading eyebrow="What we measure" title="Every marker, published before you book." intro="No panel at Republic contains a marker we cannot explain the clinical reason for." data-id="element-411" data-mp="3h07j4pngfw7f" />
          <div className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4" data-id="element-412" data-mp="2oivcc3zj57bp">
            {markerGroups.map(group => <div key={group.group} data-id="element-413" data-mp="2jj0oyaeyqzve">
                <h3 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-414" data-mp="z2j2p5ea4foi">
                  {group.group}
                </h3>
                <ul className="mt-5" data-id="element-415" data-mp="13vc5vou3nfyh">
                  {group.markers.map(marker => <li key={marker} className="rule py-3 text-sm text-ink-soft" data-id="element-416" data-mp="q3nyfyuspfnu">
                      {marker}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-417" data-mp="1msdcfe3axo4j">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-418" data-mp="1mrc3bf5ujbpg">
          <SectionHeading eyebrow="Collection" title="Two ways to get your blood drawn." data-id="element-419" data-mp="18tngi1s7xuhr" />
          <div className="mt-12 grid gap-8 md:grid-cols-2" data-id="element-420" data-mp="1drfixpqhoz07">
            {[{
            icon: HomeIcon,
            title: 'At home',
            detail: 'A kit ships within one business day. Collect in ten minutes and return it in the prepaid mailer. Results in three to five days.',
            meta: 'Included with membership'
          }, {
            icon: MapPinIcon,
            title: 'At a partner site',
            detail: 'Book a draw at one of 2,100 partner locations, usually same week. Fasting appointments available before 10am.',
            meta: 'Included with membership'
          }].map(option => <div key={option.title} className="border border-ink/15 bg-cream-light p-8" data-id="element-421" data-mp="10ki03qj2k6bh">
                <option.icon className="h-6 w-6 text-ink" strokeWidth={1.4} data-id="element-422" data-mp="wk7gvc69njeo" />
                <h3 className="mt-6 font-serif text-2xl text-ink" data-id="element-423" data-mp="rjjtr1l5rmpq">{option.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-ink-soft" data-id="element-424" data-mp="mm1l7cb1z9i9">{option.detail}</p>
                <p className="mt-6 text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-425" data-mp="3cbd9rosdltf0">
                  {option.meta}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <CtaBand title="Get the numbers first." intro="Panels are available without a membership if you would rather see your data before deciding on treatment." data-id="element-426" data-mp="2x8vag9dk6az7" />
    </main>;
}